import {
  BILLING_CURRENCY,
  DEFAULT_BILLING_INTERVAL as BILLING_INTERVAL,
  PLAN_PRICING,
} from "../billing/pricingConfig.js";
import { getPlanTitleText, getPlanTitleTranslations } from "./localizedText.js";

export { BILLING_CURRENCY, BILLING_INTERVAL };

export const TRIAL_CONFIG = Object.freeze({
  days: 14,
  plan: "pro",
  startsOn: "signup",
});

export const PLAN_IDS = Object.freeze({
  FREE: "free",
  STARTER: "starter",
  PRO: "pro",
});

export const LEGACY_PLAN_ALIASES = Object.freeze({
  enterprise: PLAN_IDS.PRO,
});

export const PLAN_ORDER = Object.freeze([
  PLAN_IDS.FREE,
  PLAN_IDS.STARTER,
  PLAN_IDS.PRO,
]);

export const CHECKOUT_PLAN_IDS = Object.freeze([
  PLAN_IDS.STARTER,
  PLAN_IDS.PRO,
]);

export const PERSISTENCE_MODES = Object.freeze({
  CLOUD: "cloud",
  LOCAL: "local",
});

export const EXPORT_FEATURE_KEYS = Object.freeze({
  JPG: "export_jpg",
  PNG: "export_png",
  PDF: "export_pdf",
  PPTX: "export_pptx",
});

const FEATURE_ORDER = Object.freeze([
  EXPORT_FEATURE_KEYS.JPG,
  EXPORT_FEATURE_KEYS.PNG,
  EXPORT_FEATURE_KEYS.PDF,
  EXPORT_FEATURE_KEYS.PPTX,
]);

const PLAN_RANK = Object.freeze(
  PLAN_ORDER.reduce((acc, planId, index) => {
    acc[planId] = index;
    return acc;
  }, {}),
);

const PLAN_CATALOG = Object.freeze({
  [PLAN_IDS.FREE]: Object.freeze({
    id: PLAN_IDS.FREE,
    title: "Free",
    pricing: PLAN_PRICING[PLAN_IDS.FREE],
    limits: Object.freeze({
      guestLocalOnly: true,
      maxRows: 10,
      exportFormats: Object.freeze(["jpg"]),
      canSaveCloudProjects: false,
      canOpenCloudProjects: false,
      canCollaborate: false,
      templateLibraryAccess: false,
      canSaveAsTemplate: false,
      savedStylePresets: 0,
      unlimitedSavedStylePresets: false,
      builtInStylePresets: true,
    }),
  }),
  [PLAN_IDS.STARTER]: Object.freeze({
    id: PLAN_IDS.STARTER,
    title: "Starter",
    pricing: PLAN_PRICING[PLAN_IDS.STARTER],
    limits: Object.freeze({
      guestLocalOnly: false,
      maxRows: 250,
      exportFormats: Object.freeze(["jpg", "png", "pdf"]),
      canSaveCloudProjects: true,
      canOpenCloudProjects: true,
      canCollaborate: true,
      templateLibraryAccess: true,
      canSaveAsTemplate: false,
      savedStylePresets: 10,
      unlimitedSavedStylePresets: false,
      builtInStylePresets: true,
    }),
  }),
  [PLAN_IDS.PRO]: Object.freeze({
    id: PLAN_IDS.PRO,
    title: "Pro",
    pricing: PLAN_PRICING[PLAN_IDS.PRO],
    limits: Object.freeze({
      guestLocalOnly: false,
      maxRows: 5000,
      exportFormats: Object.freeze(["jpg", "png", "pdf", "pptx"]),
      canSaveCloudProjects: true,
      canOpenCloudProjects: true,
      canCollaborate: true,
      templateLibraryAccess: true,
      canSaveAsTemplate: true,
      savedStylePresets: null,
      unlimitedSavedStylePresets: true,
      builtInStylePresets: true,
    }),
  }),
});

const FORMAT_TO_FEATURE = Object.freeze({
  jpg: EXPORT_FEATURE_KEYS.JPG,
  jpeg: EXPORT_FEATURE_KEYS.JPG,
  png: EXPORT_FEATURE_KEYS.PNG,
  pdf: EXPORT_FEATURE_KEYS.PDF,
  pptx: EXPORT_FEATURE_KEYS.PPTX,
});

const FEATURE_TO_FORMAT = Object.freeze({
  [EXPORT_FEATURE_KEYS.JPG]: "jpg",
  [EXPORT_FEATURE_KEYS.PNG]: "png",
  [EXPORT_FEATURE_KEYS.PDF]: "pdf",
  [EXPORT_FEATURE_KEYS.PPTX]: "pptx",
});

export const SIGNED_OUT_VIEWER_LIMITS = Object.freeze({
  maxRows: 5,
  exportFormats: Object.freeze(["jpg"]),
  canSave: false,
  canOpen: false,
  canCollab: false,
  aiCallsPerSession: 1,
});

export const SIGNED_IN_VIEWER_LIMITS = Object.freeze({
  maxRows: PLAN_CATALOG[PLAN_IDS.PRO].limits.maxRows,
  exportFormats: PLAN_CATALOG[PLAN_IDS.PRO].limits.exportFormats,
  canSave: true,
  canOpen: true,
  canCollab: true,
  aiCallsPerSession: 9999,
});

export function normalizeCurrencyCode() {
  return BILLING_CURRENCY;
}

export function normalizePlanId(
  value,
  { fallback = PLAN_IDS.FREE, allowLegacyAlias = true } = {},
) {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  if (PLAN_ORDER.includes(normalized)) {
    return normalized;
  }

  if (
    allowLegacyAlias &&
    Object.prototype.hasOwnProperty.call(LEGACY_PLAN_ALIASES, normalized)
  ) {
    return LEGACY_PLAN_ALIASES[normalized];
  }

  return PLAN_ORDER.includes(fallback) ? fallback : PLAN_IDS.FREE;
}

export function isCanonicalPlanId(value) {
  return PLAN_ORDER.includes(
    String(value || "")
      .trim()
      .toLowerCase(),
  );
}

export function isLegacyPlanAlias(value) {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  return Object.prototype.hasOwnProperty.call(LEGACY_PLAN_ALIASES, normalized);
}

export function normalizeCheckoutPlanId(value) {
  const normalized = normalizePlanId(value);
  return CHECKOUT_PLAN_IDS.includes(normalized) ? normalized : null;
}

export function isCheckoutPlanId(value) {
  return Boolean(normalizeCheckoutPlanId(value));
}

export function getPlanTitle(plan, { locale = null } = {}) {
  const normalized = normalizePlanId(plan);
  return (
    getPlanTitleText(normalized, locale) ||
    PLAN_CATALOG[normalized]?.title ||
    PLAN_CATALOG[PLAN_IDS.FREE].title
  );
}

export function getPlanCatalogEntry(plan, { locale = null } = {}) {
  const normalized = normalizePlanId(plan);
  const entry = PLAN_CATALOG[normalized];
  if (!entry) {
    return PLAN_CATALOG[PLAN_IDS.FREE];
  }

  const titleTranslations = getPlanTitleTranslations(normalized);
  if (!titleTranslations) {
    return entry;
  }

  return {
    ...entry,
    title: getPlanTitle(normalized, { locale }),
    titleTranslations,
  };
}

export function getPlanLimits(plan) {
  return getPlanCatalogEntry(plan).limits;
}

export function getPlanPrice(plan) {
  return getPlanCatalogEntry(plan).pricing;
}

export function getPlanRank(plan) {
  return PLAN_RANK[normalizePlanId(plan)] || 0;
}

export function comparePlanRank(leftPlan, rightPlan) {
  return getPlanRank(leftPlan) - getPlanRank(rightPlan);
}

export function isPaidPlanId(plan) {
  return getPlanRank(plan) > getPlanRank(PLAN_IDS.FREE);
}

export function canPersistProjectsForPlan(plan) {
  return Boolean(getPlanLimits(plan).canSaveCloudProjects);
}

export function resolvePersistenceMode({
  isSignedIn = false,
  plan = PLAN_IDS.FREE,
  hasCloudSaveEntitlement = null,
  forceLocal = false,
} = {}) {
  if (forceLocal) {
    return PERSISTENCE_MODES.LOCAL;
  }

  const cloudEntitled =
    typeof hasCloudSaveEntitlement === "boolean"
      ? hasCloudSaveEntitlement
      : canPersistProjectsForPlan(plan);

  if (isSignedIn && cloudEntitled) {
    return PERSISTENCE_MODES.CLOUD;
  }

  return PERSISTENCE_MODES.LOCAL;
}

export function canUseCloudPersistence(options = {}) {
  return resolvePersistenceMode(options) === PERSISTENCE_MODES.CLOUD;
}

export function isCloudPersistenceMode(input = {}) {
  return resolvePersistenceMode(input) === PERSISTENCE_MODES.CLOUD;
}

export function canCollaborateForPlan(plan) {
  return Boolean(getPlanLimits(plan).canCollaborate);
}

export function canAccessTemplateLibraryForPlan(plan) {
  return Boolean(getPlanLimits(plan).templateLibraryAccess);
}

export function canSaveAsTemplateForPlan(plan) {
  return Boolean(getPlanLimits(plan).canSaveAsTemplate);
}

export function canAccessBuiltInStylePresetsForPlan(plan) {
  return Boolean(getPlanLimits(plan).builtInStylePresets);
}

export function hasUnlimitedSavedStylePresetsForPlan(plan) {
  return Boolean(getPlanLimits(plan).unlimitedSavedStylePresets);
}

export function maxSavedStylePresetsForPlan(plan) {
  if (hasUnlimitedSavedStylePresetsForPlan(plan)) {
    return null;
  }
  const value = Number(getPlanLimits(plan).savedStylePresets);
  if (!Number.isFinite(value) || value < 0) {
    return 0;
  }
  return Math.floor(value);
}

export function canSaveStylePresetsForPlan(plan) {
  return (
    hasUnlimitedSavedStylePresetsForPlan(plan) ||
    maxSavedStylePresetsForPlan(plan) > 0
  );
}

export function getNextHigherPlanId(plan) {
  const normalized = normalizePlanId(plan);
  const index = PLAN_ORDER.indexOf(normalized);
  if (index < 0 || index >= PLAN_ORDER.length - 1) {
    return null;
  }
  return PLAN_ORDER[index + 1];
}

export function resolvePaywallUpgradePlan(plan) {
  return getNextHigherPlanId(plan) || PLAN_IDS.PRO;
}

export function normalizeExportFeatureKey(value) {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  return FEATURE_ORDER.includes(normalized) ? normalized : null;
}

export function normalizeExportFormat(value) {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  if (!normalized) {
    return null;
  }

  if (normalized === "jpeg") {
    return "jpg";
  }

  return Object.prototype.hasOwnProperty.call(FORMAT_TO_FEATURE, normalized)
    ? normalized
    : null;
}

export function exportFeatureFromFormat(format) {
  const normalized = normalizeExportFormat(format);
  return normalized ? FORMAT_TO_FEATURE[normalized] : null;
}

export function exportFormatFromFeature(feature) {
  const normalized = normalizeExportFeatureKey(feature);
  return normalized ? FEATURE_TO_FORMAT[normalized] : null;
}

export function allowedExportFeaturesForPlan(plan) {
  const limits = getPlanLimits(plan);
  return limits.exportFormats
    .map(
      (format) => FORMAT_TO_FEATURE[String(format || "").toLowerCase()] || null,
    )
    .filter(Boolean);
}

export function allowedExportFormatsForPlan(plan) {
  return [...getPlanLimits(plan).exportFormats];
}

export function maxRowsForPlan(plan) {
  return Number(getPlanLimits(plan).maxRows);
}

export function isFeatureAllowedForPlan(feature, plan) {
  const normalizedFeature = normalizeExportFeatureKey(feature);
  if (!normalizedFeature) {
    return false;
  }

  return allowedExportFeaturesForPlan(plan).includes(normalizedFeature);
}

export function requiredPlanForFeature(feature) {
  const normalizedFeature = normalizeExportFeatureKey(feature);
  if (!normalizedFeature) {
    return PLAN_IDS.FREE;
  }

  for (const planId of PLAN_ORDER) {
    if (isFeatureAllowedForPlan(normalizedFeature, planId)) {
      return planId;
    }
  }

  return PLAN_IDS.PRO;
}

export function requiredPlanForExportFormat(format) {
  const feature = exportFeatureFromFormat(format);
  if (!feature) {
    return null;
  }
  return requiredPlanForFeature(feature);
}

export function requiredPlanForRowCount(rowCount) {
  const normalizedRows = Number(rowCount);
  if (!Number.isFinite(normalizedRows) || normalizedRows <= 0) {
    return PLAN_IDS.FREE;
  }

  for (const planId of PLAN_ORDER) {
    if (normalizedRows <= maxRowsForPlan(planId)) {
      return planId;
    }
  }

  return PLAN_IDS.PRO;
}

export function buildExportEntitlementsForPlan(plan) {
  const normalizedPlan = normalizePlanId(plan);
  const features = FEATURE_ORDER.reduce((acc, featureKey) => {
    acc[featureKey] = isFeatureAllowedForPlan(featureKey, normalizedPlan);
    return acc;
  }, {});

  return {
    plan: normalizedPlan,
    maxRows: maxRowsForPlan(normalizedPlan),
    features,
    exportFormats: allowedExportFormatsForPlan(normalizedPlan),
  };
}

export function buildViewerEntitlementsLimits(isSignedIn) {
  return isSignedIn
    ? { ...SIGNED_IN_VIEWER_LIMITS }
    : { ...SIGNED_OUT_VIEWER_LIMITS };
}
